import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Alert, Center, Loader, Stack, Text, Title } from '@mantine/core';
import { AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Card } from '../components/ui';
import { useAuth } from '../lib/auth/useAuth';
import { colors } from '../theme/tokens';

/**
 * /auth/callback — Cognito Hosted UI redirect target.
 *
 * Amplify completes the OAuth code exchange when `signInWithRedirect()` is
 * resolved by reading the URL query params on this page. We then hydrate the
 * Zustand auth store from `fetchAuthSession()` and navigate to `/`.
 *
 * If the exchange fails (expired code, wrong client, missing tokens) we
 * surface the error and offer a link back to /login. The page never auto-
 * retries — bad codes are not transient.
 */
export const Route = createFileRoute('/auth/callback')({
  component: AuthCallback,
});

function AuthCallback() {
  const navigate = useNavigate();
  const { handleRedirectCallback } = useAuth();
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await handleRedirectCallback();
        if (!cancelled) {
          void navigate({ to: '/', replace: true });
        }
      } catch (cause) {
        if (cancelled) return;
        const message = cause instanceof Error ? cause.message : 'Sign-in failed';
        setErr(message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [handleRedirectCallback, navigate]);

  return (
    <Center pt={96}>
      <Card style={{ width: '100%', maxWidth: 420 }}>
        <Stack gap="md" align="center">
          <Title order={3} c={colors.neon}>
            Completing sign-in…
          </Title>
          {err ? (
            <>
              <Alert color="red" variant="light" icon={<AlertTriangle size={14} />}>
                {err}
              </Alert>
              <Text size="sm" c="dimmed">
                <a href="/login" style={{ color: colors.hudCyan }}>
                  Return to sign-in
                </a>
              </Text>
            </>
          ) : (
            <Loader color={colors.neon} />
          )}
        </Stack>
      </Card>
    </Center>
  );
}
